import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, Link2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { MANAGED_FLOATING_WIDGET_ATTRIBUTE } from "@/lib/floatingWidgetSuppression";
import { useDrawerWidth, type DrawerWidth } from "@/components/ui/useDrawerWidth";
import { useOptionalMenuCondense, type MenuCondenseContextValue } from "@/components/ui/menuCondense";

const COPY_LINK_FEEDBACK_MS = 2000;

/**
 * Copy a shareable link to the record this drawer is showing (the Linear "Copy issue URL"
 * affordance). Rendered automatically for every `sheetSide` drawer, so the capability arrives with
 * the component rather than being re-implemented per surface.
 *
 * It copies `window.location.href`, which is correct **because** a record drawer is required to be
 * URL-addressable (ADR 2026-07-12 rule 5 — `?peek=<id>`). On a drawer that ignores that rule this
 * copies the collection's URL, which is precisely the bug the rule exists to prevent; fix the
 * drawer, don't special-case the button.
 */
const DialogCopyLinkButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & {
    /** Rendered inside the drawer's top-right control cluster (flex row) rather than absolutely
     *  positioned on its own. */
    inCluster?: boolean;
  }
>(({ className, inCluster, ...props }, ref) => {
  const [copied, setCopied] = React.useState(false);
  const resetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // The panel can close (unmount) inside the feedback window — clear the timer so we never set
  // state on an unmounted button.
  React.useEffect(() => () => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
  }, []);

  const handleCopy = React.useCallback(async () => {
    // navigator.clipboard is absent in jsdom and on insecure origins. Bail BEFORE the optimistic
    // "Link copied" — optional chaining on the write would resolve to undefined and flip the state
    // to a copy that never happened (Codex P3). No API → stay silent, exactly as the catch does.
    if (!navigator.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      resetTimerRef.current = setTimeout(() => setCopied(false), COPY_LINK_FEEDBACK_MS);
    } catch {
      // Clipboard denied/unavailable — stay silent rather than claim a copy that didn't happen.
    }
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleCopy}
      // The label carries the state so screen readers get the confirmation the icon swap gives
      // sighted users.
      aria-label={copied ? "Link copied" : "Copy link to this record"}
      title={copied ? "Link copied" : "Copy link"}
      className={cn(
        "rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
        // In the cluster the flex parent positions it; standalone it sits left of the ✕.
        inCluster ? undefined : "absolute right-12 top-4",
        className,
      )}
      {...props}
    >
      {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      <span className="sr-only">{copied ? "Link copied" : "Copy link to this record"}</span>
    </button>
  );
});
DialogCopyLinkButton.displayName = "DialogCopyLinkButton";

/**
 * Radix does not expose the Root's `modal` value to `Content`, but every drawer opens through this
 * `Dialog` export — so we capture it here and republish it via context. `DialogContent` uses it to
 * keep the left-nav condense feature inert for MODAL drawers (ENT-3477, Codex review): a modal
 * `Dialog` renders an opaque, pointer-blocking overlay over the whole viewport, so a condensed rail
 * would be dimmed and unclickable and the drawer would leave a dead inset. Condense is meaningful
 * only for the canonical non-modal docked drawer whose page stays visible + interactive.
 */
const DialogModalContext = React.createContext<boolean>(true);

const Dialog = ({ modal = true, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogModalContext.Provider value={modal}>
    <DialogPrimitive.Root modal={modal} {...props} />
  </DialogModalContext.Provider>
);
Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

type DialogOutsideEvent = CustomEvent<{ originalEvent?: Event }>;

function isManagedFloatingWidgetOutsideEvent(event: DialogOutsideEvent) {
  const target = event.detail.originalEvent?.target;
  return target instanceof Element && Boolean(target.closest(`[${MANAGED_FLOATING_WIDGET_ATTRIBUTE}]`));
}

function preventManagedFloatingWidgetDismiss(event: DialogOutsideEvent) {
  if (isManagedFloatingWidgetOutsideEvent(event)) {
    event.preventDefault();
  }
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Modal (centered) vs docked right-drawer bases. The drawer uses the Sheet's clean
// right-slide (no zoom/fade/top-slide) so it slides smoothly on and off the right
// edge instead of flashing from the corner.
const DIALOG_CONTENT_MODAL_CLASS =
  "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg";
const DIALOG_CONTENT_RIGHT_DRAWER_CLASS =
  "fixed inset-y-0 right-0 z-50 flex h-full max-h-screen flex-col gap-4 border-l bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right";

/**
 * Dialog content. Centered modal by default; pass `sheetSide="right"` to dock it as
 * a side drawer instead.
 *
 * **Which primitive?** The discriminator is what the surface *is*, not whether it
 * already exists (see `docs/ui-ux-guidelines.md` → *Record Editing Surfaces*):
 * - `DialogContent sheetSide="right"` — a **record reached from a collection view**
 *   (table, list, card grid, heatmap), whether you are porting an existing Dialog or
 *   building a new editor. Non-blocking, record-switchable, `?peek=<id>` addressable.
 * - `Sheet` (`./sheet`) — a side panel that is **not** a record from a collection:
 *   filters, nav, a self-contained detail panel.
 * - `Drawer` (`./drawer`, Vaul) — mobile-style bottom sheet.
 * - `DialogContent` (default) — short, blocking confirmations / gates. **Never a record.**
 *
 * A record reached from a collection opens in a drawer, not a large modal (ADR
 * `docs/adr/2026-07-12-drawer-default-for-record-editing.md`). Building or porting one?
 * Follow `docs/skills/sg-modal-to-drawer/SKILL.md` — the non-obvious parts
 * (outside-click, exit animation, tab/width persistence, deep linking) are all in there.
 *
 * @param sheetSide Dock the panel to an edge instead of centering it. Uses the Sheet's
 *   clean slide-in/out (no modal zoom/fade), so it must NOT be combined with the
 *   centered-modal keyframes. Pair with `modal={false}` + a transparent
 *   `overlayClassName` for a non-blocking drawer.
 * @param overlayClassName Restyle the backdrop — e.g. `bg-transparent pointer-events-none`
 *   so a docked drawer leaves the page behind it visible and interactive.
 */
/** Default per-browser key for a drawer's remembered width. A surface can override it so two
 *  different drawers don't share one remembered width. */
const DEFAULT_DRAWER_WIDTH_STORAGE_KEY = "sg-drawer-width";

/** Fires `onOpen` on mount. Rendered inside Radix `Content` (mounted only while open), so it re-runs
 *  on each open — the hook to re-seed the drawer's default width against the live viewport. */
const DrawerOpenReseed = ({ onOpen }: { onOpen: () => void }) => {
  React.useEffect(() => {
    onOpen();
  }, [onOpen]);
  return null;
};

/**
 * Bridges a canonical drawer to the left-nav condense mediator (ENT-3477) WITHOUT either component
 * importing the other. Rendered inside `DialogPrimitive.Content`, so Radix mounts it only while the
 * drawer is open — which is exactly the "drawer open" half of the condense condition.
 *
 * While mounted AND engaged at 100% it holds a ref-counted condense request; the effect cleanup
 * (engagement ends, or the drawer closes and this unmounts) releases it, restoring the menu for
 * free with no persisted state. It also honors R10.1: when the sidebar signals a manual expansion
 * (`expandDemandSeq` changes) while engaged, it applies the 50% preset so the drawer drops to half
 * width as the menu expands. No-ops when there is no `SidebarProvider` (`condense` is null).
 */
const DrawerMenuCondenseRequest = ({
  fullEngaged,
  condense,
  setPreset,
}: {
  fullEngaged: boolean;
  condense: MenuCondenseContextValue | null;
  setPreset: (fraction: number) => void;
}) => {
  // Depend on the STABLE `requestCondense` callback, not the whole context value: the context
  // identity changes every time the ref-count moves, and depending on it would release+re-acquire
  // the request on our own acquire. `requestCondense` is a `useCallback([])`, so this effect runs
  // exactly once when engagement begins and cleans up when it ends or the drawer unmounts.
  const requestCondense = condense?.requestCondense;
  React.useEffect(() => {
    if (!requestCondense || !fullEngaged) return;
    return requestCondense();
  }, [requestCondense, fullEngaged]);

  const seq = condense?.expandDemandSeq ?? 0;
  const prevSeq = React.useRef(seq);
  React.useEffect(() => {
    if (seq === prevSeq.current) return;
    prevSeq.current = seq;
    if (fullEngaged) setPreset(0.5);
  }, [seq, fullEngaged, setPreset]);

  return null;
};

/** The 50% / 100% coverage presets — the right half of the drawer's top-right control cluster. */
const DialogWidthPresetToggle = ({ width }: { width: DrawerWidth }) => {
  const presetClass = (active: boolean) =>
    cn(
      "w-full rounded-none px-2 py-0.5 text-xs font-medium transition-colors",
      active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground",
    );
  return (
    <div
      className="inline-grid grid-cols-2 overflow-hidden rounded-md border bg-background/95 shadow-sm"
      data-testid="drawer-size-toggle"
    >
      <button type="button" className={presetClass(width.isHalf)} onClick={() => width.setPreset(0.5)} aria-pressed={width.isHalf}>
        50%
      </button>
      <button type="button" className={presetClass(width.isFull)} onClick={() => width.setPreset(1)} aria-pressed={width.isFull}>
        100%
      </button>
    </div>
  );
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    overlayClassName?: string;
    sheetSide?: "right";
    /**
     * Suppress the copy-link affordance. Only for a docked panel that is genuinely NOT a
     * URL-addressable record — the button is the default precisely so every record drawer gets a
     * shareable link without each surface reinventing one.
     */
    hideCopyLink?: boolean;
    /**
     * Suppress the 50%/100% presets + drag-resize. Only for a deliberately fixed-width docked panel
     * (a thin, single-purpose side panel). Record drawers keep them — width is a canonical drawer
     * affordance, not a per-surface reinvention. ENT-2455.
     */
    hideWidthPresets?: boolean;
    /** Per-browser key for the remembered width, so distinct drawers don't share one width. */
    widthStorageKey?: string;
  }
>(({ className, children, style, overlayClassName, sheetSide, hideCopyLink, hideWidthPresets, widthStorageKey, onInteractOutside, onPointerDownOutside, ...props }, ref) => {
  // Hooks run unconditionally; the width is only *applied* for a drawer with presets enabled.
  // A canonical drawer inside a SidebarProvider reads the collapsed rail width so its 100% preset
  // fills the viewport minus the rail (ENT-3477); with no provider the inset is 0 (today's full
  // viewport). `useOptionalMenuCondense` never throws, so provider-less surfaces just no-op.
  // The feature is inert for MODAL drawers (their opaque overlay would cover the rail): force the
  // inset to 0 and never hold a condense request, so a modal drawer's 100% stays full-viewport.
  const condense = useOptionalMenuCondense();
  const isModal = React.useContext(DialogModalContext);
  const menuCondenseEnabled = !isModal && condense != null;
  const fullInsetPx = menuCondenseEnabled ? condense.condensedInsetPx : 0;
  const width = useDrawerWidth(widthStorageKey ?? DEFAULT_DRAWER_WIDTH_STORAGE_KEY, fullInsetPx);
  const isDrawer = sheetSide === "right";
  const widthPresetsOn = isDrawer && !hideWidthPresets;
  // The component owns the width when presets are on — its value wins over any caller `style.width`
  // so the toggle/drag actually move the panel.
  const mergedStyle = widthPresetsOn
    ? { ...style, width: `${width.width}px`, maxWidth: "100vw" }
    : style;

  return (
    <DialogPortal>
      {/* overlayClassName lets a caller dim/undim the backdrop — e.g. a docked
          slide-over presentation passes a transparent, click-through overlay. */}
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          isDrawer ? DIALOG_CONTENT_RIGHT_DRAWER_CLASS : DIALOG_CONTENT_MODAL_CLASS,
          isDrawer && "overflow-hidden",
          className,
        )}
        style={mergedStyle}
        onInteractOutside={(event) => {
          preventManagedFloatingWidgetDismiss(event);
          onInteractOutside?.(event);
        }}
        onPointerDownOutside={(event) => {
          preventManagedFloatingWidgetDismiss(event);
          onPointerDownOutside?.(event);
        }}
        {...props}
      >
        {/* Radix mounts Content only when open (and unmounts it after the close animation), so this
            sentinel's mount == "the drawer just opened" — re-seed the 50% default against the live
            viewport then, since the wrapper (and the width hook) can sit mounted-but-closed with a
            stale viewport otherwise (Codex P2). */}
        {widthPresetsOn ? <DrawerOpenReseed onOpen={width.reseedDefault} /> : null}
        {/* Bridge to the left-nav condense mediator (ENT-3477): held while engaged at 100%, released
            on close/50%/expand. Same mount==open lifecycle as the re-seed sentinel above. */}
        {widthPresetsOn ? (
          <DrawerMenuCondenseRequest
            fullEngaged={width.fullEngaged}
            condense={menuCondenseEnabled ? condense : null}
            setPreset={width.setPreset}
          />
        ) : null}
        {/* Drag the left edge to resize; double-click to snap back to 50%. */}
        {widthPresetsOn ? (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            onPointerDown={width.startResize}
            onDoubleClick={width.resetWidth}
            data-testid="drawer-resize-handle"
            className="group absolute inset-y-0 left-0 z-20 flex w-2 cursor-col-resize touch-none items-center justify-center hover:bg-primary/20 active:bg-primary/30"
          >
            <span className="h-8 w-0.5 rounded-full bg-border group-hover:bg-primary" aria-hidden="true" />
          </div>
        ) : null}

        {children}

        {/* The canonical drawer top-right chrome, laid out as ONE right-aligned flex row so the
            controls keep a deterministic gap and can never crowd or paint over each other:
            [copy] [50% | 100%] [✕]. A drawer shows a record, so it is URL-addressable
            (ADR 2026-07-12 rule 5) and its width is an operator preference — both are canonical
            affordances that arrive with the component, not per-surface reinventions (ENT-2455).
            The ✕ lives INSIDE this same flex row rather than as a separate absolutely-positioned
            element, so no hardcoded offset or z-index race can leave it obstructed by the width/
            copy controls at any drawer width or zoom (ENT-3424). A centered modal keeps the plain
            absolute ✕ below and gets neither copy nor width chrome: it is a short blocking
            interaction with nothing to link to and no width to tune. */}
        {isDrawer ? (
          <div className="absolute right-4 top-3.5 z-30 flex items-center gap-2" data-testid="drawer-control-cluster">
            {!hideCopyLink ? <DialogCopyLinkButton inCluster data-testid="dialog-copy-link" /> : null}
            {widthPresetsOn ? <DialogWidthPresetToggle width={width} /> : null}
            <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
        ) : (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogContentNoClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onInteractOutside, onPointerDownOutside, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className,
      )}
      onInteractOutside={(event) => {
        preventManagedFloatingWidgetDismiss(event);
        onInteractOutside?.(event);
      }}
      onPointerDownOutside={(event) => {
        preventManagedFloatingWidgetDismiss(event);
        onPointerDownOutside?.(event);
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContentNoClose.displayName = "DialogContentNoClose";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogContentNoClose,
  DialogCopyLinkButton,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
